// SignInPage — unauthenticated landing page.
// Mirrors the hubguard pattern: a full-page card with a single magic-link button.
// RequireAuth stashes the attempted URL in location.state.from; we thread it
// through to the backend as ?return_to= so WorkOS can redirect back after auth.

import { useLocation } from "react-router-dom";
import { LoginButton } from "@avagency/frontend/components/auth/LoginButton";
import { Footer } from "../components/Footer";

interface LocationState {
  from?: { pathname: string; search?: string; hash?: string };
}

function buildSignInUrl(returnTo: string): string {
  const url = new URL("/api/v1/auth/signin", window.location.origin);
  url.searchParams.set("return_to", returnTo);
  return url.toString();
}

export function SignInPage() {
  const location = useLocation();
  const state = location.state as LocationState | null;
  // Preserve pathname + search + hash so post-signin redirect lands exactly
  // where the user was headed (e.g. /recoveries?filter=open#row-42).
  const from = state?.from;
  const returnTo = from
    ? `${from.pathname}${from.search ?? ""}${from.hash ?? ""}`
    : "/";
  const signInUrl = buildSignInUrl(returnTo);

  return (
    <div className="dundial-auth-bg flex min-h-svh flex-col items-center justify-center gap-8 p-4">
      <div className="relative z-10 flex w-full max-w-sm flex-col gap-6">
        {/* Brand lockup */}
        <div className="flex flex-col items-center gap-3 text-center">
          {/* Mini orb */}
          <div
            aria-hidden
            className="h-10 w-10 rounded-full"
            style={{
              background: "conic-gradient(from 220deg, #f4f6fa, #8b94a6 30%, #1a2647 55%, #f4f6fa 80%, #cfd6e2)",
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,.2), 0 4px 20px rgba(139,108,239,.4)",
            }}
          />
          <div>
            <h1 className="text-2xl font-bold tracking-tight" style={{ color: "#f4f6fa" }}>
              DunDial
            </h1>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest" style={{ color: "#6e7a8e" }}>
              Stripe dunning tuner
            </p>
          </div>
        </div>

        {/* Sign-in card */}
        <div className="dundial-auth-card rounded-xl p-6">
          <div className="space-y-4">
            <div className="space-y-1 text-center">
              <h2 className="text-base font-semibold" style={{ color: "#f4f6fa" }}>Sign in to your workspace</h2>
              <p className="text-sm" style={{ color: "#8b94a6" }}>
                We'll send a magic link to your work email.
              </p>
            </div>
            <LoginButton startUrl={signInUrl} className="w-full">
              Sign in with magic link
            </LoginButton>
          </div>
        </div>
      </div>
      <div className="relative z-10">
        <Footer />
      </div>
    </div>
  );
}
